import { ConsumableLine, ConsumablePricingContext, ConsumablePricingResult } from '../consumable-models';
import { BaseConsumableRule } from '../consumable-rule-base';

/**
 * Rule: Volume-Based Pricing
 * 
 * Handles cases where materials are priced by volume (ml) rather than by unit count.
 * For example: A pack of 10 vials × 4ml each = 40ml total, doctor uses 1ml.
 * 
 * Calculation:
 * - Total volume in pack = standardPacksize × volumePerUnit
 * - Price per ml = singleExitPrice / totalVolume
 * - Apply doctor markup (if not network doctor)
 * - Total = (price per ml) * volume dispensed
 * Note: VAT is applied separately after pricing calculations
 */
export class RuleVolumeBasedPricing extends BaseConsumableRule {
    name = 'Volume-Based Pricing';
    priority = 0; // Run first - highest priority for volume-based materials
    
    applies(line: ConsumableLine, ctx: ConsumablePricingContext): boolean {
        // Apply to consumables with volume information (medications like syrups, or materials)
        // Check if rxUnit indicates volume-based (ML) or if we have volume metrics
        const rxUnit = (line.rxUnit || '').toUpperCase();
        const hasVolumeMetric = line.product?.volumeMetric3 && 
                                line.product?.unitOfMeasure3 === 'ML';
        const hasDispensingVolume = line.product?.dispensingVolumePacksize;
        // Also check if we have volumePerUnit stored in the line (set by component)
        const hasVolumePerUnit = (line as any).volumePerUnit;
        
        // Apply if:
        // 1. rxUnit indicates ML (volume-based), OR
        // 2. Has volumeMetric3 with ML unit, OR
        // 3. Has dispensingVolumePacksize, OR
        // 4. Has volumePerUnit stored in line
        const isVolumeBased = rxUnit.includes('ML') || 
                             hasVolumeMetric || 
                             hasDispensingVolume || 
                             hasVolumePerUnit;
        
        return line.codeType === 'consumable' &&
               !!isVolumeBased;
    }
    
    apply(line: ConsumableLine, ctx: ConsumablePricingContext): ConsumablePricingResult {
        const warnings: string[] = [];
        
        // Get volume per unit from available sources (priority: stored volumePerUnit > volumeMetric3 > dispensingVolumePacksize)
        let volumePerUnit = (line as any).volumePerUnit || 0;
        
        if (volumePerUnit <= 0 && line.product) {
            // Try volumeMetric3 first
            if (line.product.volumeMetric3 && line.product.unitOfMeasure3 === 'ML') {
                volumePerUnit = parseFloat(line.product.volumeMetric3) || 0;
            }
            
            // Fall back to dispensingVolumePacksize
            if (volumePerUnit <= 0 && line.product.dispensingVolumePacksize) {
                const dispensingVol = line.product.dispensingVolumePacksize;
                volumePerUnit = typeof dispensingVol === 'string' 
                    ? parseFloat(dispensingVol) || 0
                    : dispensingVol;
            }
        }
        
        if (volumePerUnit <= 0) {
            warnings.push('Volume information not available, using standard pack pricing');
            // Fall back to standard calculation
            const basePricePerUnit = this.calculateBasePricePerUnit(line);
            let pricePerUnit = basePricePerUnit;
            if (!ctx.isNetworkDoctor && ctx.modifier) {
                pricePerUnit = this.applyMarkup(pricePerUnit, ctx.modifier);
            }
            const calculatedTotalPrice = pricePerUnit * line.quantity;
            return {
                line: { ...line },
                calculatedUnitPrice: pricePerUnit,
                calculatedTotalPrice,
                trace: ['Volume information not available, using standard pricing'],
                warnings
            };
        }
        
        // Calculate total volume in pack
        // For syrups: standardPacksize (1) × volumePerUnit (100ml) = 100ml total
        // For vials: standardPacksize (10) × volumePerUnit (4ml) = 40ml total
        const totalVolumeInPack = line.standardPacksize * volumePerUnit;
        
        // Calculate price per ml
        const pricePerMl = (line.singleExitPrice || 0) / totalVolumeInPack;
        
        // Apply doctor markup if not a network doctor
        let finalPricePerMl = pricePerMl;
        if (!ctx.isNetworkDoctor && ctx.modifier) {
            finalPricePerMl = this.applyMarkup(finalPricePerMl, ctx.modifier);
        }
        
        // Calculate total for the dispensed volume
        const volumeDispensed = line.quantity || 0;
        const calculatedTotalPrice = finalPricePerMl * volumeDispensed;
        
        // Warn if dispensing more than total pack volume
        if (volumeDispensed > totalVolumeInPack) {
            warnings.push(
                `Dispensing ${volumeDispensed}ml exceeds total pack volume of ${totalVolumeInPack}ml. ` +
                `Consider using multiple line items for multiple packs.`
            );
        }
        
        const trace = [
            `Volume-based pricing: Pack contains ${line.standardPacksize} × ${volumePerUnit}ml = ${totalVolumeInPack}ml`,
            `Price per ml: R${pricePerMl.toFixed(4)} (pack: R${line.singleExitPrice} / ${totalVolumeInPack}ml)`,
            ...(ctx.modifier && !ctx.isNetworkDoctor 
                ? [`After markup (${ctx.modifier}%): R${finalPricePerMl.toFixed(4)} per ml`]
                : []),
            `Total for ${volumeDispensed}ml: R${calculatedTotalPrice.toFixed(2)} (VAT will be applied separately)`
        ];
        
        return {
            line: { ...line },
            calculatedUnitPrice: finalPricePerMl,
            calculatedTotalPrice,
            trace,
            warnings
        };
    }
}

